import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const navigate = useNavigate();

    const [pw, setPw] = useState("");
    const [confirm, setConfirm] = useState("");
    const [done, setDone] = useState(false);
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (pw !== confirm) {
            toast.error("Passwords do not match");
            return;
        }
        if (!token) {
            toast.error("Missing reset token");
            return;
        }
        setLoading(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: pw });
            setDone(true);
            setTimeout(() => navigate("/login"), 2500);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-md mx-auto px-4 sm:px-6 py-16" data-testid="reset-password-page">
            <h1 className="font-display text-3xl sm:text-4xl font-bold">Choose a new password</h1>
            <p className="text-muted-foreground mt-2">Pick a password of at least 6 characters.</p>

            {done ? (
                <div className="mt-8 rounded-2xl border border-primary/20 bg-primary/5 p-6 text-sm" data-testid="reset-done">
                    <CheckCircle2 className="w-6 h-6 text-primary" />
                    <div className="font-display text-lg font-semibold mt-3">Password updated</div>
                    <p className="text-muted-foreground mt-2">Redirecting you to login…</p>
                </div>
            ) : (
                <form onSubmit={submit} className="mt-8 bg-white border border-border/60 rounded-2xl p-6 space-y-4">
                    <div>
                        <Label htmlFor="npw">New password</Label>
                        <Input id="npw" type="password" required minLength={6} data-testid="reset-new-input"
                            className="mt-1 rounded-xl h-11" value={pw} onChange={(e) => setPw(e.target.value)} />
                    </div>
                    <div>
                        <Label htmlFor="cpw">Confirm password</Label>
                        <Input id="cpw" type="password" required minLength={6} data-testid="reset-confirm-input"
                            className="mt-1 rounded-xl h-11" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
                    </div>
                    <Button type="submit" disabled={loading || !pw || !confirm} data-testid="reset-submit-btn"
                        className="w-full rounded-full h-11 bg-primary hover:bg-primary/90">
                        {loading ? "Updating…" : "Reset password"}
                    </Button>
                    <div className="text-xs text-center text-muted-foreground">
                        <Link to="/login" className="hover:text-foreground">Back to login</Link>
                    </div>
                </form>
            )}
        </div>
    );
}
